import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Function to wake OpenClaw and trigger sync
const wakeOpenClaw = async (authToken) => {
  try {
    console.log("Waking OpenClaw for sync...");
    const response = await axios.post(`${API}/openclaw/wake`, {}, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    console.log("OpenClaw wake response:", response.data);
    return response.data;
  } catch (error) {
    console.error("OpenClaw wake error:", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [openclawSyncing, setOpenclawSyncing] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
          
          // Wake OpenClaw in background (non-blocking)
          setOpenclawSyncing(true);
          wakeOpenClaw(storedToken).finally(() => setOpenclawSyncing(false));
        } catch (error) {
          console.error("Auth init error:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Wake OpenClaw in background (non-blocking)
    setOpenclawSyncing(true);
    wakeOpenClaw(access_token).finally(() => setOpenclawSyncing(false));
    
    return userData;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Wake OpenClaw in background (non-blocking)
    setOpenclawSyncing(true);
    wakeOpenClaw(access_token).finally(() => setOpenclawSyncing(false));
    
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const currentToken = localStorage.getItem("token");
    return {
      Authorization: `Bearer ${currentToken || token}`
    };
  }, [token]);

  const syncWithOpenClaw = useCallback(async () => {
    if (!token) return null;
    setOpenclawSyncing(true);
    const result = await wakeOpenClaw(token);
    setOpenclawSyncing(false);
    return result;
  }, [token]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    openclawSyncing,
    login,
    register,
    logout,
    getAuthHeaders,
    syncWithOpenClaw
  }), [user, token, loading, openclawSyncing, login, register, logout, getAuthHeaders, syncWithOpenClaw]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
